/**
 * NT1 · নোটিফিকেশন কেন্দ্র — `/notifications`
 *
 * Transcribed from `design/Oprappo P2 Projects and Explore.dc.html`, frame NT1
 * (360 × 680), lines 152–225. Every value below is the frame's.
 *
 * Frame note, line 226: "Five items where fourteen events occurred — the digest row says
 * so out loud. Category label above the headline, not an icon: nine categories cannot be
 * told apart by glyph, and a wrong guess about urgency is worse than reading a word."
 *
 * ── OFF-PALETTE, TRANSCRIBED AS DRAWN ────────────────────────────────────────
 * Line 165 sets the unread `action_required` row on `#FDF6E8`, which is not one of
 * docs/02's tokens — token-lint flags it, and it is kept as drawn per docs/01 § "when
 * the design looks wrong": flag it, build it. It is the warm panel tint that recurs
 * across seven frames (P0 Screens 2093, P0 States 354/412, Responsive 293, UX Audit
 * 55/212, Handoff 116, Architecture 447/481/649/1036), always paired with `#8A5A0C` or
 * `#B07A16` as its border. NT1 uses the fill WITHOUT a border, and `#B07A16` does not
 * appear in this frame at all — reported, because the brief expected both here.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * `GET /v1/notifications` supplies the title, the action, the three tabs, the footer and
 * every row — including `label_bn` (the category word above the headline), `time_bn`, and
 * the three derived fields the frame draws: the dot, the title weight and the warm tint.
 * All three are derived SERVER-SIDE from the read state and the category
 * (`apps/api/src/notifications/screens.ts`), so two clients cannot disagree about which
 * row is shouting — and the amber dot stays the screen's one amber element.
 *
 * `সব পড়া হয়েছে` is `POST /read-all` followed by a re-read, not a local flag. The bell in
 * every other screen's app bar carries the same count from the same route; a screen that
 * marked its own rows read would leave the bell saying two.
 *
 * The surface is `account`, not a column of its own: NT1 is a P2 screen and the P0 matrix
 * has no notification column, so `states/INTEGRATION.md § 3` assigns it the column it
 * functionally is.
 *
 * ── MONEY, NOT UNIFIED ───────────────────────────────────────────────────────
 * Line 184 draws `৳ ১,১৯০` in Bangla digits inside a `'Anek Bangla'` run; line 195
 * draws `৳ ১,৩৪০` the same way. `formatTaka` from `@oprappo/contracts` produces Latin
 * digits (`৳ 1,190`) and `formatTakaBn` produces these — the two normative sources
 * disagree and the question is open, so each frame is transcribed exactly as it draws
 * its own amount and nothing is unified. Both strings sit in `font-anek`: Manrope has
 * no Bangla digits and would fall back silently at the wrong size (docs/02 § numerals).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, RowChevron, SAND_FRAME } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import {
  fetchNotifications,
  markAllRead,
  type NotificationCentre,
  type NotificationView,
} from './api.js';

/**
 * docs/13 § UI: "every `action_required` item shows what is blocked and on whom."
 *
 * Thrown rather than rendered around, in the house style of `@oprappo/ui`'s
 * `OprappoPropError`. An `action_required` row missing either fact renders as a demand
 * with no subject, which is the one thing this category exists not to be — and by render
 * time there is nothing to do about it. The API refuses to write such a row
 * (`BlockedContextRequiredError`); this is the same rule at the other end.
 */
export class BlockedContextMissingError extends Error {
  constructor(id: string, missing: string) {
    super(
      `notification ${id} is action_required but carries no ${missing}. docs/13 § UI: ` +
        `"every action_required item shows what is blocked and on whom."`,
    );
    this.name = 'BlockedContextMissingError';
  }
}

/** The two lines a row renders. For `action_required` they are the two required facts. */
export function rowLines(item: NotificationView): { titleBn: string; bodyBn: string } {
  if (item.category !== 'action_required') {
    return { titleBn: item.title_bn, bodyBn: item.body_bn };
  }
  if (item.blocked_what_bn === null || item.blocked_what_bn.length === 0) {
    throw new BlockedContextMissingError(item.id, 'blocked_what_bn (what is blocked)');
  }
  if (item.blocked_on_bn === null || item.blocked_on_bn.length === 0) {
    throw new BlockedContextMissingError(item.id, 'blocked_on_bn (on whom it is blocked)');
  }
  return { titleBn: item.blocked_what_bn, bodyBn: item.blocked_on_bn };
}

/**
 * The row dot — lines 166, 177 and 188.
 *
 * `attention` is `background:#E8A33D`, `unread` is `background:#0E3A45`, `read` is
 * `border:1.5px solid #C4BFB2` with no fill. All three are `8 × 8`, `border-radius:50%`,
 * `margin-top:7px`, `flex:none`. The radius is one of the two docs/02 allows (avatars and
 * pills); everything else on this screen is square.
 */
function Dot({ kind }: { kind: NotificationView['dot'] }): JSX.Element {
  const fill =
    kind === 'attention'
      ? 'bg-amber'
      : kind === 'unread'
        ? 'bg-teal'
        : // `box-content` is not decoration, and it is the same rule `../offering/chrome.js`'s
          // `Radio` records: the design canvas sets `box-sizing:border-box` on the frame
          // boxes and NOWHERE else, so `width:8px` + `border:1.5px` there is an ELEVEN-pixel
          // box — ten as Chrome paints it, which floors a 1.5px border to one device pixel
          // per side. This app's reset makes everything `border-box`, which drew the read
          // dot 8px wide and started the text column two pixels left of the frame's 39px on
          // every read row. Measured, not assumed: the frame's own rect is 10.00 × 10.00.
          'border-[1.5px] border-border-warm box-content';
  return <span className={`w-[8px] h-[8px] rounded-full mt-[7px] flex-none ${fill}`} aria-hidden />;
}

function Row({
  item,
  last,
  onOpen,
}: {
  item: NotificationView;
  last: boolean;
  onOpen: (() => void) | undefined;
}): JSX.Element {
  const { titleBn, bodyBn } = rowLines(item);

  const inner = (
    <>
      <Dot kind={item.dot} />
      <span className="flex-1 block">
        <span className="flex justify-between items-baseline gap-[10px]">
          <span
            className={
              'block font-anek font-semibold text-[13px] leading-[1.75] ' +
              (item.dot === 'attention' ? 'text-amber-text' : 'text-muted')
            }
          >
            {item.label_bn}
          </span>
          {/*
            * `null` renders nothing rather than a placeholder. A row written before
            * `notifications.time_bn` existed has no drawn timestamp, and formatting
            * `created_at` here would put Bangla this codebase composed on every row of
            * the screen (0021 § the column).
            */}
          <span className="block font-anek font-normal text-[13px] leading-[1.75] text-muted flex-none">
            {item.time_bn ?? ''}
          </span>
        </span>
        <span
          className={
            'block font-anek text-[15.5px] leading-[1.75] text-ink mt-[3px] ' +
            (item.title_weight === 600 ? 'font-semibold' : 'font-medium')
          }
        >
          {titleBn}
        </span>
        <span className="block font-anek font-normal text-[14px] leading-[1.75] text-body mt-[2px]">
          {bodyBn}
        </span>
      </span>
    </>
  );

  // `padding:14px 16px;display:flex;gap:12px`, plus `border-bottom` on all but the last
  // row (line 209 draws no border) and `background:#FDF6E8` on line 165 only.
  const shell =
    'px-[16px] py-[14px] flex gap-[12px] w-full text-left' +
    (last ? '' : ' border-b border-border') +
    (item.highlighted ? ' bg-warning-surface' : '');

  // The frame draws every row as a plain block with no chevron and no pressed state, so
  // a row without a deep link is not a control. A row that HAS one becomes a button with
  // byte-identical styling — docs/13 defines `action_route` and NT1 draws no affordance
  // for it, which is reported rather than resolved by adding a glyph the frame lacks.
  if (onOpen === undefined) return <div className={shell}>{inner}</div>;
  return (
    <button type="button" onClick={onOpen} className={`${shell} bg-transparent rounded-none`}>
      {inner}
    </button>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function NT1NotificationsView({
  data,
  onMarkAllRead,
}: {
  data: NotificationCentre | null;
  onMarkAllRead: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<string>('all');

  // The tabs filter by category, which is a fact already on every row — no second read,
  // and no client deciding what `কাজ দরকার` means. `সব পড়া হয়েছে` is a WRITE followed by
  // a re-read: marking rows read locally would show a screen the server disagrees with,
  // and the bell's count comes from the same read.
  const items = (data?.notifications ?? []).filter((i) => tab === 'all' || i.category === tab);

  return (
    <Frame className={SAND_FRAME}>
      {/* `background:#fff;padding:16px 16px 0;border-bottom:1px solid #E2DED5` */}
      <div className="bg-white px-[16px] pt-[16px] border-b border-border">
        <div className="flex justify-between items-center mb-[14px]">
          <h1 className="font-anek font-semibold text-[22px] leading-[1.25] text-ink m-0">
            {data?.title_bn ?? ''}
          </h1>
          <button
            type="button"
            onClick={onMarkAllRead}
            className="font-anek font-medium text-[14px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none"
          >
            {data?.mark_all_read_bn ?? ''}
          </button>
        </div>
        <div className="flex">
          {(data?.tabs ?? []).map((t, i) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                aria-current={active ? 'true' : undefined}
                className={
                  // `padding:0 3px 11px` — `pt-0` is load-bearing, not tidying: the UA
                  // stylesheet gives `button` `padding:1px 6px`, styles.css's reset does
                  // not zero it, and `px-`/`pb-` leave the top alone. That 1px made the
                  // tab strip 39.25px against the frame's 38.25px and pushed every row
                  // below the header down by one, which reads as a whole-screen shift.
                  'pt-0 px-[3px] pb-[11px] bg-transparent border-0 rounded-none font-anek text-[15px] leading-[1.75] ' +
                  (i === (data?.tabs.length ?? 0) - 1 ? '' : 'mr-[20px] ') +
                  (active
                    ? 'border-b-2 border-teal mb-[-1px] font-semibold text-teal'
                    : 'font-medium text-muted')
                }
              >
                {t.label_bn}
              </button>
            );
          })}
        </div>
      </div>

      {/* `flex:1;overflow:hidden` */}
      <div className="flex-1 oprappo-scroll">
        {items.map((item, i) => (
          <Row
            key={item.id}
            item={item}
            last={i === items.length - 1}
            onOpen={
              item.action_route === null
                ? undefined
                : () => {
                    navigate(item.action_route as string);
                  }
            }
          />
        ))}
      </div>

      {/* Line 221 — the footer that leads to NT2. */}
      <button
        type="button"
        onClick={() => navigate('/account/notifications')}
        className="bg-sand border-0 border-t border-border px-[16px] py-[12px] flex justify-between items-center gap-[11px] w-full text-left rounded-none"
      >
        <span className="font-anek font-normal text-[13px] leading-[1.75] text-body">
          {data?.prefs_link_bn ?? ''}
        </span>
        <RowChevron size={14} />
      </button>
    </Frame>
  );
}

export function NT1Notifications(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchNotifications(signal), []);
  const { data, error, reload } = useResource(load, 'notifications');

  return (
    <SurfaceStates surface="account" error={error ?? undefined}>
      <NT1NotificationsView data={data} onMarkAllRead={() => void markAllRead().then(reload)} />
    </SurfaceStates>
  );
}
